import dayjs from 'dayjs';
import { SUBSIDIARIES } from './constants';

const subsidiaryLabels = {};
SUBSIDIARIES.forEach((s) => { subsidiaryLabels[s.value] = s.label; });

// 角色标识（与 auth/constants.js ROLES 保持一致，此处用字面量避免引入图标依赖）
const ROLE_SUBSIDIARY = 'subsidiary';

const ts = (offset, unit = 'hour') => dayjs().subtract(offset, unit).format('YYYY-MM-DD HH:mm');

// ============================================================
// 集团管理员通知
// 定位：集团绿色供应链管理部（归口管理）+ 采购与供应链管理委员会（最高决策）
// 职责侧重：统筹统一库、发布通用标准/汇总名录/负面清单、指导监督检查各级
//   子企业、审批集团年度评价报告、审批子公司上报/提报事项、组织总部采购准入
// 通知内容聚焦：上报/提报待审批、跨子公司监控、监督检查、汇总发布、重大决策
// ============================================================
function buildGroupNotifications() {
  const list = [];

  // 1. 子公司上报/提报的待审批事项（集团行使审批与决策职责）
  const approvalNotices = [
    { title: '黑名单提报待审批', content: '「资环链金再生资源有限公司」提报了供应商「华再生环保科技有限公司」列入不良行为供应商库的请求，请审核证据并决策。', type: 'approval', priority: 'high', link: '/approval' },
    { title: '黑名单提报待审批', content: '「中国资源循环集团电池有限公司」提报了供应商「格林美金属回收有限公司」列入负面清单的请求，请审核后决策。', type: 'approval', priority: 'high', link: '/approval' },
    { title: '黑名单申诉待裁决', content: '「中环再生资源利用有限公司」对列入不良行为供应商库提起申诉，需集团归口部门复核裁决。', type: 'approval', priority: 'high', link: '/approval' },
    { title: '总部采购准入待审批', content: '集团总部采购项目「再生原料检测服务」的供应商「鑫源有色金属冶炼有限公司」准入评审已完成，请审批入库。', type: 'approval', priority: 'urgent', link: '/approval' },
  ];
  approvalNotices.forEach((n, i) => {
    list.push({ id: `g_approval_${i + 1}`, ...n, date: ts(i * 2), read: i > 2 });
  });

  // 2. 年度评价报告审批 / 汇总名录发布（委员会决策 + 归口部门发布职责）
  const decisionNotices = [
    { title: '集团年度履约评价报告待审批', content: '2025 年度集团供应商履约评价报告已汇总完成，请采购与供应链管理委员会审批。', type: 'approval', priority: 'high', link: '/approval' },
    { title: '待汇总发布合格供应商名录', content: '已有 5 家子企业报备本公司「合格供应商名录」，请汇总并发布集团「合格供应商汇总名录」。', type: 'info', priority: 'medium', link: '/evaluation/grading' },
    { title: '待发布负面清单', content: '本期新增 3 家触发负面清单情形的供应商，请审核后在集团范围统一通报发布。', type: 'info', priority: 'high', link: '/blacklist' },
  ];
  decisionNotices.forEach((n, i) => {
    list.push({ id: `g_decision_${i + 1}`, ...n, date: ts(2 + i, 'day'), read: i > 0 });
  });

  // 3. 跨子公司风险监控（集团统筹数据监管，关注重大/共性风险）
  const riskNotices = [
    { title: '跨公司关联方风险', content: '监测到「新力塑料再生利用有限公司」在 2 家子企业供应商库中存在未披露关联关系，已要求相关单位核查。', type: 'risk', priority: 'urgent', link: '/risk' },
    { title: '重大财务风险', content: '「融汇再生能源科技有限公司」资产负债率达 85%，涉及 3 家子企业在执行合同，请统筹研判处置。', type: 'risk', priority: 'urgent', link: '/risk' },
    { title: '高风险供应商预警', content: '「天洁环保工程有限公司」近 3 批次质量不合格率超 15%，已在多家子企业出现，建议纳入重点监控。', type: 'risk', priority: 'high', link: '/risk' },
  ];
  riskNotices.forEach((n, i) => {
    list.push({ id: `g_risk_${i + 1}`, ...n, date: ts(3 + i * 6), read: i > 1 });
  });

  // 4. 监督检查（指导、监督、检查各级子企业供应商管理工作的执行情况）
  const superviseNotices = [
    { title: '子企业管理执行待督查', content: '「中国资源循环集团机动车有限公司」近 3 个月未更新供应商动态监控数据，建议开展专项检查。', type: 'warning', priority: 'medium', link: '/audit-log' },
    { title: '专项审计提醒', content: '本季度供应商管理专项审计计划待启动，请确定抽查的子企业名单与范围。', type: 'warning', priority: 'medium', link: '/audit-log' },
    { title: '名录报备超期提醒', content: '「中国资源循环集团绿色纤维有限责任公司」年度合格供应商名录尚未向集团报备，请督促上报。', type: 'warning', priority: 'medium', link: '/evaluation/grading' },
  ];
  superviseNotices.forEach((n, i) => {
    list.push({ id: `g_supervise_${i + 1}`, ...n, date: ts(1 + i, 'day'), read: i > 0 });
  });

  // 5. 系统通知（共性）
  const sysNotices = [
    { title: '系统维护通知', content: '统一供应商管理平台将于本周六 22:00-24:00 进行例行维护，届时暂停服务。', type: 'system', priority: 'low', link: null },
  ];
  sysNotices.forEach((n, i) => {
    list.push({ id: `g_sys_${i + 1}`, ...n, date: ts(2 + i, 'day'), read: true });
  });

  return list.sort((a, b) => b.date.localeCompare(a.date));
}

// ============================================================
// 子公司管理员通知
// 定位：各级子企业采购管理部门（执行机构）
// 职责侧重：本公司供应商开发/考察/准入评审/信息维护、日常动态监控、年度评价
//   分级、履约跟踪/问题整改/不良行为提报、受理投诉申诉、数据上报、落实集团部署
// 通知内容聚焦：本公司执行待办、资质/履约/评价提醒、提报结果反馈、集团统一部署
// ============================================================
function buildSubsidiaryNotifications(user) {
  const list = [];
  const myName = user?.subsidiaryName || '本公司';

  // 1. 本公司准入执行待办（开发、考察、准入评审）
  const admissionNotices = [
    { title: '准入申请待评审', content: '「华再生环保科技有限公司」提交了标准准入申请，请组织资料审查与形式评审。', type: 'approval', priority: 'high', link: '/admission' },
    { title: '现场考察待安排', content: '「鑫源有色金属冶炼有限公司」准入进入待现场考察环节，请安排评审小组实地考察。', type: 'approval', priority: 'medium', link: '/admission' },
  ];
  admissionNotices.forEach((n, i) => {
    list.push({ id: `s_admission_${i + 1}`, ...n, date: ts(i * 2), read: i > 0 });
  });

  // 2. 资质到期提醒（本公司供应商信息动态维护，需提醒供应商续期）
  const qualNotices = [
    { title: '资质即将到期', content: '本公司供应商「绿洲环保材料有限公司」安全生产许可证将于 15 天后到期，请提醒供应商续期。', type: 'warning', priority: 'urgent', link: '/archive' },
    { title: '资质已过期', content: '本公司供应商「金诚废旧金属回收有限公司」危险废物经营许可证已过期，请立即跟进处理。', type: 'warning', priority: 'urgent', link: '/archive' },
    { title: '资质即将到期', content: '本公司供应商「华再生环保科技有限公司」ISO9001 认证将于 30 天后到期。', type: 'warning', priority: 'medium', link: '/archive' },
  ];
  qualNotices.forEach((n, i) => {
    list.push({ id: `s_qual_${i + 1}`, ...n, date: ts(1 + i * 4), read: i > 0 });
  });

  // 3. 履约跟踪与问题整改（具体实施履约跟踪、问题反馈、约谈整改）
  const perfNotices = [
    { title: '抽检不合格待整改', content: '本公司供应商「天洁环保工程有限公司」最近一批次抽检合格率 68%，已开具整改通知，请跟踪闭环。', type: 'warning', priority: 'high', link: '/performance/issues' },
    { title: '交付延迟待跟进', content: '本公司供应商「万邦再生资源回收有限公司」近 3 个月累计延迟交付 5 次，建议约谈并记录。', type: 'warning', priority: 'high', link: '/performance' },
    { title: '整改即将到期', content: '问题单 XJ-2026-018 整改期限剩余 1 天，请督促供应商完成整改并验收。', type: 'warning', priority: 'medium', link: '/performance/issues' },
  ];
  perfNotices.forEach((n, i) => {
    list.push({ id: `s_perf_${i + 1}`, ...n, date: ts(2 + i * 5), read: i > 1 });
  });

  // 4. 评价分级执行待办（组织日常动态监控、年度综合评价与分级）
  const evalNotices = [
    { title: '定期评价到期', content: '2026 年第二季度定期评价即将到期，请组织本公司供应商评价工作。', type: 'info', priority: 'medium', link: '/evaluation/daily' },
    { title: '年度评价待启动', content: '本公司年度供应商综合评价将于下月启动，请成立评价小组并准备数据。', type: 'info', priority: 'medium', link: '/evaluation/annual' },
    { title: '评价结果待确认', content: '「格林美金属回收有限公司」日常评价结果待确认，请及时处理。', type: 'info', priority: 'low', link: '/evaluation/daily' },
  ];
  evalNotices.forEach((n, i) => {
    list.push({ id: `s_eval_${i + 1}`, ...n, date: ts(1 + i, 'day'), read: i > 0 });
  });

  // 5. 本公司风险预警待处理
  const riskNotices = [
    { title: '本公司高风险预警', content: '本公司供应商「天洁环保工程有限公司」触发质量高风险预警，请指派处理人并制定措施。', type: 'risk', priority: 'urgent', link: '/risk' },
    { title: '关联方排查提醒', content: '本公司供应商「新力塑料再生利用有限公司」疑似存在未披露关联关系，请开展排查并上报。', type: 'risk', priority: 'high', link: '/risk' },
  ];
  riskNotices.forEach((n, i) => {
    list.push({ id: `s_risk_${i + 1}`, ...n, date: ts(4 + i * 6), read: i > 0 });
  });

  // 6. 投诉申诉受理（受理并处理供应商的日常投诉与申诉）
  const feedbackNotices = [
    { title: '供应商异议待受理', content: '「瑞丰再生塑料科技有限公司」对评价结果提出异议，请受理并核实处理。', type: 'info', priority: 'medium', link: '/evaluation/grading' },
  ];
  feedbackNotices.forEach((n, i) => {
    list.push({ id: `s_feedback_${i + 1}`, ...n, date: ts(8 + i * 3), read: false });
  });

  // 7. 提报/上报结果反馈（集团对子公司提报事项的审批结果）
  const resultNotices = [
    { title: '黑名单提报已通过', content: '您提报的供应商「华再生环保科技有限公司」列入不良行为供应商库请求已获集团批准，供应商已加入黑名单。', type: 'info', priority: 'medium', link: '/blacklist' },
    { title: '黑名单提报被驳回', content: '您提报的供应商「中环再生资源利用有限公司」黑名单请求被集团驳回，原因：证据不充分，请补充材料后重新提报。', type: 'warning', priority: 'high', link: '/blacklist' },
  ];
  resultNotices.forEach((n, i) => {
    list.push({ id: `s_result_${i + 1}`, ...n, date: ts(4 + i * 5), read: i > 0 });
  });

  // 8. 集团统一部署（落实集团关于供应商管理的各项统一部署与协调要求）
  const groupDeployNotices = [
    { title: '集团统一部署', content: `集团绿色供应链管理部发布新版《供应商准入通用标准》，请${myName}对照执行并完成自查。`, type: 'system', priority: 'high', link: '/announcement' },
    { title: '名录报备要求', content: `请${myName}于本月底前向集团绿色供应链管理部报备年度「合格供应商名录」。`, type: 'system', priority: 'medium', link: '/evaluation/grading' },
    { title: '负面清单通报', content: '集团已发布最新负面清单，请核查本公司供应商库并停止与清单内供应商开展新合作。', type: 'system', priority: 'high', link: '/blacklist' },
  ];
  groupDeployNotices.forEach((n, i) => {
    list.push({ id: `s_deploy_${i + 1}`, ...n, date: ts(1 + i, 'day'), read: i > 1 });
  });

  // 9. 系统通知（共性）
  const sysNotices = [
    { title: '系统维护通知', content: '统一供应商管理平台将于本周六 22:00-24:00 进行例行维护，届时暂停服务。', type: 'system', priority: 'low', link: null },
  ];
  sysNotices.forEach((n, i) => {
    list.push({ id: `s_sys_${i + 1}`, ...n, date: ts(3 + i, 'day'), read: true });
  });

  return list.sort((a, b) => b.date.localeCompare(a.date));
}

// 按角色返回对应的消息通知列表
export function getNotificationsForUser(user) {
  if (user?.role === ROLE_SUBSIDIARY) {
    return buildSubsidiaryNotifications(user);
  }
  // 默认（集团管理员）
  return buildGroupNotifications();
}

// 向后兼容：默认导出集团视角通知
export const notifications = buildGroupNotifications();
